import React, { useCallback, useEffect, useState } from 'react';
import { PageSection, PageSectionVariants, EmptyState, Title, EmptyStateBody, Drawer, DrawerContent, DrawerContentBody, TreeViewDataItem } from '@patternfly/react-core';
import { AngleLeftIcon, AngleRightIcon } from '@patternfly/react-icons'

import { useDispatch, useSelector } from 'react-redux';

import { removeModelCustomizationPageFocus, StoreState } from '../../store/reducers';

import './customization-page.css';
import CustomizationSideBar from './CustomizationSideBar';
import CustomizationForm from './CustomizationForm';
import CustomizationToolbar from './CustomizationToolbar';
import { selectCustomizationBaseIds, selectCustomizationOutputIds } from '../../store/selectors';
import { CustomizationObjectType } from '../../store/model-customization-types';
import FullRoiDetailsModal from './FullRoiDetailsModal';
import { useTranslation } from 'react-i18next';
import { uniq } from "lodash-es";

/** Recursively find treeview items under and including target. */
const getActiveTreeItemsUnderTarget = (item: TreeViewDataItem, target: TreeViewDataItem, branch: TreeViewDataItem[], isTargetBranch: boolean = false) => {
    const isInTargetBranch = isTargetBranch || item.id === target.id;

    if (isInTargetBranch) {
        branch.push(item);
    }

    if (item.children) {
        item.children.forEach(c => {
            getActiveTreeItemsUnderTarget(c, target, branch, isInTargetBranch);
        });
    }
}

/** Recursively find and collect tree view items from a specific branch up to a set target item. */
const getActiveTreeViewItemBranch = (item: TreeViewDataItem, target: TreeViewDataItem, branch: TreeViewDataItem[]): boolean => {
    if (item.children) {
        let childContained = false;
        item.children.forEach(c => {
            const wasChildContained = getActiveTreeViewItemBranch(c, target, branch);
            childContained = childContained || wasChildContained;
        });

        if (childContained) {
            // collect the branch that we're going down if this one has the child object that's our target
            branch.push(item);
        }
    }

    if (item.id === target.id) {
        branch.push(item);
        return true;
    }

    return false;
}

/** Find a single item from tree view hierarchy. */
const findItemFromHierarchy = (hierarchy: TreeViewDataItem[], id: string, mustBeLeafNode: boolean = false): TreeViewDataItem | undefined => {
    const nextItems = [...hierarchy];
    while (nextItems.length > 0) {
        const next = nextItems.pop();
        if (next) {
            if (next.id && next.id === id && (!mustBeLeafNode || !next.children)) {
                return next;
            }
            if (next.children) { next.children.forEach(c => nextItems.push(c)); }
        }
    }

    return undefined;
}

const CustomizationPage = () => {

    const appClient = useSelector((state: StoreState) => state.currentAppClient);

    const { t } = useTranslation();
    const dispatch = useDispatch();


    const baseIds = useSelector((state: StoreState) => selectCustomizationBaseIds(state));
    const outputIds = useSelector((state: StoreState) => selectCustomizationOutputIds(state));
    const customizationFetchError = useSelector((state: StoreState) => state.customizationFetchError);
    const modelCustomizationPageFocusId = useSelector((state: StoreState) => state.modelCustomizationPageFocusId);

    /** ID of the item we're currently focusing on (on what we last clicked on) */
    const [currentTreeViewFocus, setCurrentTreeViewFocus] = useState('');

    const [activeTreeViewItems, setActiveTreeViewItems] = useState<TreeViewDataItem[]>([]);
    const [treeViewData, setTreeViewData] = useState<TreeViewDataItem[]>([]);
    const appConfig = useSelector((state: StoreState) => state.appConfig);
    const [isDrawerExpanded, setIsDrawerExpanded] = React.useState(true);

    const handleTreeViewItemSelect = useCallback((item: TreeViewDataItem) => {

        // TODO: some of this code updates the 'defaultExpanded' prop directly into objects already in state
        // which is not optimal behaviour.

        // are we selecting or unselecting an item from the current active selection?
        const isSelecting = activeTreeViewItems.find(i => i.id === item.id) === undefined;

        // is this a leaf item? we have some special rules for those.
        const isLeafItem = item.children === undefined;

        // don't allow de-selecting leaf items
        if (!isSelecting && isLeafItem) {
            return;
        }

        // prepare existing tree view selections
        const branchItems: TreeViewDataItem[] = [];
        activeTreeViewItems.forEach(item => {
            if (item.children === undefined) {
                // immediately unselect any leaf items -- this fixes some visual glitches
                item.defaultExpanded = false;
            } else {
                // include non-leaf items in the next active selection
                branchItems.push(item);
            }
        });

        if (!isSelecting) {
            const activeTreeViewItemBranch: TreeViewDataItem[] = [];
            treeViewData.forEach(t => getActiveTreeItemsUnderTarget(t, item, activeTreeViewItemBranch));
            activeTreeViewItemBranch.forEach(i => i.defaultExpanded = false);

            setActiveTreeViewItems(branchItems.filter(i => !activeTreeViewItemBranch.find(branchItem => branchItem.id === i.id)));
        } else {
            const activeTreeViewItemBranch: TreeViewDataItem[] = [];
            treeViewData.forEach(t => getActiveTreeViewItemBranch(t, item, activeTreeViewItemBranch));
            activeTreeViewItemBranch.forEach(i => i.defaultExpanded = true);

            setActiveTreeViewItems(uniq(branchItems.concat(activeTreeViewItemBranch)));
        }

        // set latest focus
        setCurrentTreeViewFocus(isLeafItem && item.id ? item.id : '');

    }, [activeTreeViewItems, treeViewData]);

    const handleTreeViewDataUpdate = useCallback((data: TreeViewDataItem[]) => {

        // update tree view data with branches and leaves that should start expanded
        const nextItems = [...data];
        while (nextItems.length > 0) {
            const next = nextItems.pop();
            if (next) {
                if (next.children) { next.children.forEach(c => nextItems.push(c)); }
                if (activeTreeViewItems.find(i => i.id && next.id && i.id === next.id)) {
                    next.defaultExpanded = true;
                }
            }
        }

        setTreeViewData(data);
    }, [activeTreeViewItems]);

    useEffect(() => {
        // change active tab when signaled by redux store (basically when duplicating or removing models)
        if (modelCustomizationPageFocusId !== undefined && modelCustomizationPageFocusId !== currentTreeViewFocus) {

            // see if we can change the actual selection yet, otherwise keep waiting for next updates
            const item = findItemFromHierarchy(treeViewData, modelCustomizationPageFocusId, true);
            if (item) {
                dispatch(removeModelCustomizationPageFocus());
                setCurrentTreeViewFocus(modelCustomizationPageFocusId);
                handleTreeViewItemSelect(item);
            }
        }
    }, [dispatch, currentTreeViewFocus, modelCustomizationPageFocusId, treeViewData, handleTreeViewItemSelect]);

    // detect what type of view we're currently presenting
    let currentViewType = CustomizationObjectType.None;
    const isAnOutputCurrentlySelected = outputIds.includes(currentTreeViewFocus);
    if (isAnOutputCurrentlySelected) {
        currentViewType = CustomizationObjectType.CustomizationOutput;
    } else if (baseIds.includes(currentTreeViewFocus)) {
        currentViewType = CustomizationObjectType.CustomizationBase;
    }

    return (
        <>

            {appConfig && appConfig.showErrorsInUi && customizationFetchError !== null ? (
                <PageSection variant={PageSectionVariants.light}>
                    <EmptyState>
                        <Title headingLevel="h4" size="lg">{t('error.generic')}</Title>
                        <EmptyStateBody>{t('error.customizationFetchFailed', { customizationFetchError: customizationFetchError })}</EmptyStateBody>
                    </EmptyState>
                </PageSection>
            ) : (
                <>
                    <PageSection variant={PageSectionVariants.light} padding={{ default: 'noPadding' }}>

                        <Drawer isExpanded={isDrawerExpanded} isInline position="left" className="sidebar-drawer">
                            <DrawerContent panelContent={(
                                <CustomizationSideBar
                                    onTreeViewDataUpdate={handleTreeViewDataUpdate} treeViewData={treeViewData}
                                    onTreeViewItemSelect={handleTreeViewItemSelect}
                                    activeTreeViewItems={activeTreeViewItems}
                                />)}>
                                <DrawerContentBody>

                                    <div className="sidebar-drawer-handle-bg">
                                        <div className="sidebar-drawer-handle-container" onClick={() => { setIsDrawerExpanded(!isDrawerExpanded) }}>
                                            <div className="sidebar-drawer-handle">
                                                <span>{isDrawerExpanded ? <AngleLeftIcon size='sm' /> : <AngleRightIcon size='sm' />}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="sidebar-drawer-content">
                                        <CustomizationToolbar
                                            currentView={currentViewType}
                                            customizationOutputId={currentTreeViewFocus}
                                        />
                                        <FullRoiDetailsModal />
                                        <PageSection variant={PageSectionVariants.light}>
                                            {appClient === undefined ? (
                                                <EmptyState>
                                                    <Title headingLevel="h4" size="lg">{t('selectConfigurationTarget.title')}</Title>
                                                    <EmptyStateBody>{t('selectConfigurationTarget.message')}</EmptyStateBody>
                                                </EmptyState>
                                            ) : isAnOutputCurrentlySelected ? (
                                                <CustomizationForm
                                                    customizationOutputId={currentTreeViewFocus}
                                                // modelValidation={activeValidation!} 
                                                />
                                            ) : (
                                                <EmptyState>
                                                    <EmptyStateBody>{t('customizationPage.selectCustomization.message')}</EmptyStateBody>
                                                </EmptyState>
                                            )
                                            }
                                        </PageSection>
                                    </div>
                                </DrawerContentBody>
                            </DrawerContent>
                        </Drawer>
                    </PageSection>
                </>
            )
            }

        </>
    );
}

export default CustomizationPage;
